import { useState, useCallback, useEffect } from "react";
import { Link } from "wouter";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Copy, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { checkPassword, type StrengthResult } from "./passwordUtils";
import { copyText } from "@/lib/copyText";

/** Unbiased random index (rejection sampling, no modulo bias). */
function randomIndex(n: number): number {
  const limit = Math.floor(0x100000000 / n) * n;
  const buf = new Uint32Array(1);
  do crypto.getRandomValues(buf); while (buf[0] >= limit);
  return buf[0] % n;
}

const TOOL_INFO_CONTENT = {
  what: `Generates a secure random password and separately checks the strength of an existing password with a pattern-based estimator (it recognises common words, names, keyboard runs, repeats, dates and letter substitutions like p@ssw0rd, not just character types).`,
  how: `Use the generator to create a new password with your chosen length and character types, or paste an existing password into the checker to see its estimated strength.`,
  tip: `A strength checker can only evaluate patterns, not whether a password has already been leaked in a data breach — using a unique password for every account matters at least as much as the password's raw complexity.`,
};

export default function PasswordStrength() {
  const [password, setPassword] = useState("");
  const [length, setLength] = useState([16]);
  const [upper, setUpper] = useState(true);
  const [lower, setLower] = useState(true);
  const [nums, setNums] = useState(true);
  const [syms, setSyms] = useState(true);

  const [genError, setGenError] = useState("");
  const { logUsageNow } = useLogUsageOnAction();

  // Typing/pasting a password into the field above to check its
  // strength stays free -- that's live feedback on input you already
  // have, not something this tool generated. Only the Generate button
  // (creates a brand-new password) is gated.
  const generate = useCallback(async () => {
    let chars = "";
    if (upper) chars += "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    if (lower) chars += "abcdefghijklmnopqrstuvwxyz";
    if (nums) chars += "0123456789";
    if (syms) chars += "!@#$%^&*()_+-=[]{}|;:,.<>?";
    if (!chars) { setGenError("Turn on at least one character type to generate a password."); return; }
    setGenError("");
    const allowed = await logUsageNow();
    if (!allowed) return;
    const sets = [
      upper && "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
      lower && "abcdefghijklmnopqrstuvwxyz",
      nums && "0123456789",
      syms && "!@#$%^&*()_+-=[]{}|;:,.<>?",
    ].filter(Boolean) as string[];
    let out = "";
    // Retry until every selected character type appears at least once.
    do {
      out = Array.from({ length: length[0] }, () => chars[randomIndex(chars.length)]).join("");
    } while (length[0] >= sets.length && !sets.every(set => Array.from(out).some(c => set.includes(c))));
    setPassword(out);
  }, [length, upper, lower, nums, syms, logUsageNow]);

  const [strength, setStrength] = useState<StrengthResult | null>(null);
  useEffect(() => {
    if (!password) {
      setStrength(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      checkPassword(password).then(r => {
        if (!cancelled) setStrength(r);
      });
    }, 150);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [password]);

  return (
    <ToolContainer>
      <div className="space-y-6">
        <div className="space-y-2">
          <Label>Password</Label>
          <div className="flex gap-2">
            <Input aria-label="Password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter or generate a password" className="font-mono" />
            <Button variant="outline" size="icon" aria-label="Copy password" onClick={() => { void copyText(password, "Copied!"); }}><Copy className="h-4 w-4" /></Button>
          </div>
          {password && strength && (
            <div className="space-y-1">
              <div className="h-2 rounded-full bg-muted overflow-hidden"><div className={`h-full rounded-full transition-all ${strength.color}`} style={{ width: `${strength.pct}%` }} /></div>
              <p className="text-sm font-medium">{strength.label}</p>
              <p className="text-xs text-muted-foreground">
                Estimated time to crack offline (slow hash): {strength.crackTime}. Checked in your browser; nothing is sent anywhere.
              </p>
              {strength.warning && <p className="text-sm text-destructive">{strength.warning}</p>}
              {strength.suggestions.length > 0 && (
                <ul className="text-sm list-disc pl-5 text-muted-foreground">
                  {strength.suggestions.map(x => <li key={x}>{x}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
        <div className="space-y-3 p-4 rounded-lg border bg-card">
          <Label>Generate Password</Label>
          <div className="space-y-2"><Label className="text-sm">Length: {length[0]}</Label><Slider aria-label="Password length" value={length} onValueChange={setLength} min={4} max={64} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2"><Switch checked={upper} onCheckedChange={setUpper} aria-label="Uppercase" /><span className="text-sm">Uppercase</span></div>
            <div className="flex items-center gap-2"><Switch checked={lower} onCheckedChange={setLower} aria-label="Lowercase" /><span className="text-sm">Lowercase</span></div>
            <div className="flex items-center gap-2"><Switch checked={nums} onCheckedChange={setNums} aria-label="Numbers" /><span className="text-sm">Numbers</span></div>
            <div className="flex items-center gap-2"><Switch checked={syms} onCheckedChange={setSyms} aria-label="Symbols" /><span className="text-sm">Symbols</span></div>
          </div>
          {genError && <p role="alert" className="text-xs text-destructive">{genError}</p>}
          <Button onClick={generate} className="gap-2"><RefreshCw className="h-4 w-4" /> Generate</Button>
          <p className="text-xs text-muted-foreground" data-testid="pwc-generator-link">Want passphrases of random words, several at once, look-alike characters left out or the entropy maths? Use the <Link href="/tool/password-generator" className="underline">Password Generator</Link>. This page is for checking a password you already have.</p>
        </div>
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
